import classNames from 'classnames';
import { useAtom } from 'jotai';
import Block, { BlockEmbed } from 'quill/blots/block';
import Quill, { Delta } from 'quill/core';
import type { CSSProperties, MouseEventHandler } from 'react';
import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useDebouncedCallback } from 'use-debounce';
import type { GutterButtonProps } from './Gutter';
import SVGIcon from '../components/SVGIcon';
import dragIcon from '../icons/non-prefilled/16x16/drag.svg';
import { getScrollContainer } from '../post/listenToPostScroll';
import useEventListener from '../utils/useEventListener';
import useBlotHighlightedClass from './controls/toolbar/useBlotHighlightedClass';
import getDropPlaceholderPosition from './getDropPlaceholderPosition';
import { isDraggingAtom } from './reorderGutterAtoms';
import * as styles from './ReorderGutterButton.module.scss';
import ReorderSnapshot from './ReorderSnapshot';
import type { Blot } from './types';
import DraggingAutoScroll from './utils/DraggingAutoScroll';
import getReferenceNodeForBounds from './utils/getReferenceNodeForBounds';

export type DraggableBlot = Block | BlockEmbed;

const getButtonHorizontalPosition = (activeBlot: DraggableBlot) => {
  if (!activeBlot) return null;
  if (activeBlot.statics.blotName === 'list') {
    const left = parseInt(
      getComputedStyle(activeBlot.domNode as Element).paddingLeft,
      10,
    );
    return { padding: Math.max(0, left - 24), offset: 0 };
  }
  // @ts-expect-error
  if (typeof activeBlot.getReferenceNodeForBounds === 'function') {
    const { left } =
      getReferenceNodeForBounds(activeBlot).getBoundingClientRect();
    const containerLeft =
      activeBlot.scroll.domNode.getBoundingClientRect().left;
    const paddingLeft = parseInt(
      getComputedStyle(activeBlot.scroll.domNode).paddingLeft,
      10,
    );

    const formats = activeBlot.formats();
    if (formats.float === 'right' || formats.span === 'full') {
      return { padding: 0, offset: left - containerLeft - paddingLeft };
    }
    return { padding: left - containerLeft - paddingLeft, offset: 0 };
  }
  return { padding: 0, offset: 0 };
};

interface BaseReorderGutterButtonProps {
  quill: Quill;
  activeBlot: DraggableBlot;
  onDraggingStart?: (draggedBlot: DraggableBlot, position: Position) => void;
}

export const BaseReorderGutterButton = ({
  quill,
  activeBlot,
  onDraggingStart,
}: BaseReorderGutterButtonProps) => {
  useBlotHighlightedClass(activeBlot);

  const [padding, setPadding] = useState(0);
  const [offset, setOffset] = useState(0);

  const repositionButton = useCallback((blot: DraggableBlot) => {
    const position = getButtonHorizontalPosition(blot);
    if (position) {
      setPadding(position.padding);
      setOffset(position.offset);
    }
  }, []);

  // React blots are rendered asynchronously after content changes. Besides that,
  // a user operation can trigger text_change event multiple times (change image size
  // will change three attributes: width, float, span) so we debounce here.
  const debouncedRepositionButton = useDebouncedCallback(repositionButton, 200);

  useEffect(() => {
    repositionButton(activeBlot);

    const listener = () => debouncedRepositionButton(activeBlot);
    quill.on(Quill.events.TEXT_CHANGE, listener);
    return () => {
      quill.off(Quill.events.TEXT_CHANGE, listener);
    };
  }, [activeBlot, quill, repositionButton, debouncedRepositionButton]);

  const handleMouseDown: MouseEventHandler = (e) => {
    if (e.buttons !== 1) {
      return;
    }

    e.preventDefault();
    onDraggingStart?.(activeBlot, { left: e.clientX, top: e.clientY });
  };

  return (
    <div
      className={classNames(
        styles.container,
        styles[activeBlot.statics.blotName],
      )}
      style={{ transform: `translateX(${offset}px)` }}
      data-testid="reorderGutterButton"
    >
      <div
        className={styles.button}
        onMouseDown={activeBlot && handleMouseDown}
        style={{
          width: `calc(100% + ${padding}px)`,
        }}
      >
        <SVGIcon size="small">{dragIcon}</SVGIcon>
      </div>
    </div>
  );
};

type Position = { left: number; top: number };

type State =
  | {
      width?: undefined;
      paddingLeft?: undefined;
      initial?: undefined;
      position?: undefined;
      draggedBlots?: undefined;
    }
  | {
      width: number;
      paddingLeft: number;
      initial: Position;
      position: Position;
      draggedBlots: DraggableBlot[];
    };

const initialState: State = {};

type Action =
  | {
      type: 'start';
      payload: {
        width: number;
        paddingLeft: number;
        position: Position;
        initial: Position;
        draggedBlots: DraggableBlot[];
      };
    }
  | { type: 'end' }
  | { type: 'move'; payload: { left: number; top: number } };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'start':
      return action.payload;
    case 'end':
      return initialState;
    case 'move':
      return state.initial
        ? {
            ...state,
            position: {
              left: action.payload.left - state.initial.left,
              top: action.payload.top - state.initial.top,
            },
          }
        : state;
    default:
      return state;
  }
};

const useAutoScroll = (quill: Quill, isDragging: boolean) => {
  useEffect(() => {
    if (!isDragging) return undefined;

    const scrollingContainer = getScrollContainer();
    if (!scrollingContainer) return undefined;
    const draggingAutoScroll = new DraggingAutoScroll(scrollingContainer);
    draggingAutoScroll.start();

    const listener = (event: MouseEvent) => {
      draggingAutoScroll.setMouseYOffset(event.clientY);
    };
    scrollingContainer.addEventListener('mousemove', listener);
    return () => {
      draggingAutoScroll.stop();
      scrollingContainer.removeEventListener('mousemove', listener);
    };
  }, [quill, isDragging]);
};

interface ReorderGutterButtonProps extends GutterButtonProps {
  children?: (options: {
    onDraggingStart?: (
      draggedBlots: DraggableBlot[],
      position: Position,
    ) => void;
  }) => React.ReactNode;
  revealDropTarget?: (
    target: DraggableBlot,
    draggedBlots: DraggableBlot[],
  ) => DraggableBlot;
}

const ReorderGutterButton = ({
  quill,
  activeBlot,
  buttonTopOffset,
  onLockedChange,
  children,
  revealDropTarget,
}: ReorderGutterButtonProps) => {
  const [isDragging, setIsDragging] = useAtom(isDraggingAtom);
  useAutoScroll(quill, isDragging);

  const [state, dispatch] = useReducer(reducer, initialState);

  const dropTarget = useRef<DraggableBlot | null>(null);
  const lastBlot = useRef<Blot | null>(null);

  const [dropPlaceholderPosition, setDropPlaceholderPosition] =
    useState<CSSProperties | null>(null);

  const finishDragging = () => {
    dispatch({ type: 'end' });
    document.body.classList.remove(styles.dragging);
    setDropPlaceholderPosition(null);
    setIsDragging(false);
    onLockedChange(false);
  };

  useEffect(() => {
    if (!state.draggedBlots) {
      return undefined;
    }
    const elements = state.draggedBlots.map((blot) => blot.domNode as Element);
    elements.forEach((element) => {
      element.classList.add(styles.reorderGhost);
    });
    return () => {
      elements.forEach((element) => {
        element.classList.remove(styles.reorderGhost);
      });
    };
  }, [state.draggedBlots]);

  useEventListener(
    document.body,
    'mousemove',
    (e) => {
      dispatch({ type: 'move', payload: { left: e.clientX, top: e.clientY } });

      // @ts-expect-error
      const blot = quill.lineFromYOffset(e.clientY);
      if (blot === lastBlot.current) return;
      lastBlot.current = blot;

      if (blot instanceof Block || blot instanceof BlockEmbed) {
        const newTarget = state.draggedBlots?.some(
          (draggedBlot) => draggedBlot === blot,
        )
          ? state.draggedBlots[state.draggedBlots.length - 1]
          : blot;
        dropTarget.current = newTarget || null;

        if (!dropTarget.current) return;
        setDropPlaceholderPosition(
          getDropPlaceholderPosition(quill, dropTarget.current),
        );
      }
    },
    { skip: !state.draggedBlots },
  );

  useEventListener(
    document.body,
    'keydown',
    ({ key }) => {
      if (key === 'Escape') {
        finishDragging();
      }
    },
    { skip: !state.draggedBlots },
  );

  useEventListener(
    document.body,
    'contextmenu',
    () => {
      finishDragging();
    },
    { skip: !state.draggedBlots },
  );

  useEventListener(
    document.body,
    'mouseup',
    () => {
      const { draggedBlots } = state;

      finishDragging();

      if (!dropTarget.current || !draggedBlots?.[0]) return;

      const isNoop =
        dropTarget.current === draggedBlots[0].prev ||
        draggedBlots.includes(dropTarget.current);
      if (isNoop) return;

      const index = draggedBlots[0].offset(quill.scroll);
      const activeBlotContent = draggedBlots.reduce(
        (content, blot) => content.concat(blot.delta()),
        new Delta(),
      );

      const removingDelta = new Delta()
        .retain(index)
        .delete(activeBlotContent.length());

      const target = revealDropTarget
        ? revealDropTarget(dropTarget.current, draggedBlots)
        : dropTarget.current;

      const insertPosition = target.offset(quill.scroll) + target.length();

      const isLastBlotEmbed =
        draggedBlots[draggedBlots.length - 1] instanceof BlockEmbed;
      const insertingDelta =
        isLastBlotEmbed && insertPosition === quill.getLength()
          ? activeBlotContent.insert('\n')
          : activeBlotContent;

      const transformedInsertPosition =
        removingDelta.transformPosition(insertPosition);

      quill.updateContents(
        removingDelta.compose(
          new Delta().retain(transformedInsertPosition).concat(insertingDelta),
        ),
        Quill.sources.USER,
      );

      if (quill.hasFocus()) {
        quill.setSelection(transformedInsertPosition);
      }
    },
    { skip: !state.draggedBlots },
  );

  const handleDraggingStart = (
    draggedBlots: DraggableBlot[],
    { left, top }: Position,
  ) => {
    const rect = quill.root.getBoundingClientRect();
    const paddingLeft = parseInt(getComputedStyle(quill.root).paddingLeft, 10);
    const paddingRight = parseInt(
      getComputedStyle(quill.root).paddingRight,
      10,
    );
    dispatch({
      type: 'start',
      payload: {
        width: rect.width - paddingLeft - paddingRight,
        paddingLeft,
        draggedBlots,
        position: { left: rect.left, top: rect.top },
        initial: { left: left - rect.left, top: top - rect.top },
      },
    });
    document.body.classList.add(styles.dragging);
    onLockedChange(true);
    setIsDragging(true);
  };

  const dragHandler = children?.({
    onDraggingStart: handleDraggingStart,
  }) ?? (
    <BaseReorderGutterButton
      quill={quill}
      activeBlot={activeBlot}
      onDraggingStart={(blot, position) =>
        handleDraggingStart([blot], position)
      }
    />
  );

  return (
    <>
      {!isDragging && (
        <div style={{ transform: `translateY(${buttonTopOffset}px)` }}>
          {dragHandler}
        </div>
      )}
      {dropPlaceholderPosition != null &&
        createPortal(
          <div
            className={styles.dropPlaceholder}
            style={dropPlaceholderPosition}
          />,
          quill.container,
        )}
      {state.position &&
        createPortal(
          <div
            className={styles.snapshotContainer}
            style={{
              left: state.position.left + state.paddingLeft,
              top: state.position.top + buttonTopOffset,
            }}
          >
            <div className={styles.snapshotDragHandler}>{dragHandler}</div>
            <div style={{ width: state.width }}>
              <ReorderSnapshot blots={state.draggedBlots} />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default ReorderGutterButton;
